import { motion, AnimatePresence } from "motion/react";
import { CheckCircle2, XCircle, HelpCircle, ChevronRight, Sparkles } from "lucide-react";
import { HalfCircle } from "@/components/ui/HalfCircle";
import { ExperienceButton } from "./ExperienceButton";
import { useState, useEffect, useCallback } from "react";
import { CitationBadges, CitationModal } from "@/components/citations";
import type { GroundingSource } from "@/services/api";

type Score = "yes" | "partially" | "no" | "insufficient";

interface InteractiveCriterionProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  accentColor?: string;
  description: string;
  whyItMatters: string[];
  score: Score;
  assessment: string;
  tradeoffGain: string;
  tradeoffLose: string;
  questions?: string[];
  sources?: GroundingSource[];
  onNext: () => void;
  currentIndex: number;
  totalCount: number;
  skipExplanations?: boolean;
}

export function InteractiveCriterion({
  title,
  subtitle,
  icon,
  color,
  accentColor,
  description,
  whyItMatters,
  score,
  assessment,
  tradeoffGain,
  tradeoffLose,
  questions,
  sources,
  onNext,
  currentIndex,
  totalCount,
  skipExplanations = false,
}: InteractiveCriterionProps) {
  const [stage, setStage] = useState<"explanation" | "assessment">(
    skipExplanations ? "assessment" : "explanation"
  );

  const [selectedCitations, setSelectedCitations] = useState<GroundingSource[]>([]);
  const [selectedCitationIndex, setSelectedCitationIndex] = useState(0);
  const [isCitationModalOpen, setIsCitationModalOpen] = useState(false);

  const handleCitationClick = useCallback((sources: GroundingSource[], index: number) => {
    setSelectedCitations(sources);
    setSelectedCitationIndex(index);
    setIsCitationModalOpen(true);
  }, []);

  const handleCloseCitationModal = useCallback(() => {
    setIsCitationModalOpen(false);
    setSelectedCitations([]);
  }, []);

  const scoreConfig = {
    yes: {
      icon: CheckCircle2,
      label: "CRITERION MET",
      color: "text-emerald-400",
      bgColor: "bg-emerald-500/12",
      borderColor: "border-emerald-500/25",
    },
    partially: {
      icon: ({ className }: { className?: string }) => <HalfCircle className={className} />,
      label: "PARTIALLY MET",
      color: "text-amber-400",
      bgColor: "bg-amber-500/12",
      borderColor: "border-amber-500/25",
    },
    no: {
      icon: XCircle,
      label: "NOT MET",
      color: "text-rose-400",
      bgColor: "bg-rose-500/12",
      borderColor: "border-rose-500/25",
    },
    insufficient: {
      icon: HelpCircle,
      label: "INSUFFICIENT INFO",
      color: "text-slate-400",
      bgColor: "bg-slate-500/12",
      borderColor: "border-slate-500/25",
    }
  };

  const config = scoreConfig[score];
  const ScoreIcon = config.icon;

  useEffect(() => {
    setStage(skipExplanations ? "assessment" : "explanation");
  }, [currentIndex, skipExplanations]);

  const handleReveal = () => {
    setStage("assessment");
  };

  return (
    <div className="min-h-screen bg-[#063F6B] text-white py-14 px-6 flex items-center justify-center relative overflow-hidden">
      {/* Subtle radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] bg-[#124C77]/35 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-5xl w-full relative z-10">
        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          className="mb-10"
        >
          <div className="flex items-center justify-between text-sm text-slate-400 mb-2.5">
            <span>Criterion {currentIndex + 1} of {totalCount}</span>
            <span>{Math.round(((currentIndex + 1) / totalCount) * 100)}%</span>
          </div>
          <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[#EAA059] to-[#d4893e] rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((currentIndex + 1) / totalCount) * 100}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {stage === "explanation" && (
            <motion.div
              key="explanation"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <div className="rounded-2xl p-7 sm:p-8 lg:p-12 border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm">
                {/* Header */}
                <div className="flex items-center gap-5 mb-6">
                  <div className={`p-4 bg-gradient-to-br ${color} rounded-xl shadow-lg`}>
                    {icon}
                  </div>
                  <div>
                    <motion.h1
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15, duration: 0.6 }}
                      className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight"
                    >
                      {title}
                    </motion.h1>
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.25, duration: 0.5 }}
                      className="text-lg mt-1"
                      style={{ color: accentColor || '#D4893E' }}
                    >
                      {subtitle}
                    </motion.p>
                  </div>
                </div>

                {/* Core Question */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                  className="text-2xl sm:text-3xl text-white font-medium mb-4 leading-snug"
                >
                  {description}
                </motion.div>

                {/* Subtitle as description */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.35, duration: 0.6 }}
                  className="text-lg text-slate-300/80 mb-10 leading-relaxed"
                >
                  {subtitle}
                </motion.p>

                {/* Why It Matters */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.45, duration: 0.5 }}
                  className="mb-12"
                >
                  <h3 className="text-xs font-semibold text-[#EAA059]/80 uppercase tracking-widest mb-5">
                    Why This Matters
                  </h3>
                  <div className="space-y-5">
                    {whyItMatters.map((point, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.55 + index * 0.1, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                        className="flex gap-4 items-start"
                      >
                        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-[#EAA059] to-[#d4893e] flex items-center justify-center text-xs font-semibold shadow-sm">
                          {index + 1}
                        </div>
                        <p className="text-lg text-slate-300/90 leading-relaxed flex-1 pt-0.5">
                          {point}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Reveal Button */}
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55 + whyItMatters.length * 0.1 + 0.15, duration: 0.6 }}
                  className="text-center"
                >
                  <ExperienceButton
                    onClick={handleReveal}
                    className="px-10 py-4 bg-gradient-to-r from-[#EAA059] to-[#d4893e] hover:from-[#d4914d] hover:to-[#c07830] text-white text-lg font-medium rounded-full transition-all duration-300 shadow-lg shadow-[#EAA059]/20 hover:shadow-xl hover:shadow-[#EAA059]/30"
                  >
                    Reveal Assessment
                    <Sparkles className="ml-2 w-5 h-5" />
                  </ExperienceButton>
                </motion.div>
              </div>
            </motion.div>
          )}

          {stage === "assessment" && (
            <motion.div
              key="assessment"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -60 }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <div className="space-y-6">
                {/* Score + Assessment */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="rounded-2xl p-6 sm:p-7 lg:p-8 border border-white/[0.08] bg-white/[0.04] backdrop-blur-sm"
                >
                  {/* Score banner */}
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className={`flex flex-col sm:flex-row sm:items-center gap-4 p-5 rounded-xl ${config.bgColor} border ${config.borderColor} mb-6`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`p-2 bg-gradient-to-br ${color} rounded-xl shadow-md flex-shrink-0`}>
                        {icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h2>
                        <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>
                        <p className="text-base text-slate-300/80 mt-1.5 italic">{description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 sm:ml-4">
                      <ScoreIcon className={`w-8 h-8 sm:w-10 sm:h-10 ${config.color}`} />
                      <span className={`text-lg sm:text-xl font-semibold ${config.color} whitespace-nowrap`}>
                        {config.label}
                      </span>
                    </div>
                  </motion.div>

                  <h3 className="text-xs font-semibold text-slate-400/80 uppercase tracking-widest mb-3">
                    Assessment
                  </h3>
                  <p className="text-lg text-slate-200/90 leading-relaxed">
                    {assessment}
                    <CitationBadges sources={sources} onCitationClick={handleCitationClick} />
                  </p>
                </motion.div>

                {/* Trade-offs */}
                {(tradeoffGain || tradeoffLose) && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="grid md:grid-cols-2 gap-5"
                  >
                    {tradeoffGain && (
                      <div className="bg-emerald-500/8 border border-emerald-500/20 rounded-2xl p-6 hover:bg-emerald-500/12 transition-colors duration-300">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                          <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-widest">
                            What You Gain
                          </h4>
                        </div>
                        <p className="text-slate-200/90 leading-relaxed">
                          {tradeoffGain}
                        </p>
                      </div>
                    )}
                    {tradeoffLose && (
                      <div className="bg-rose-500/8 border border-rose-500/20 rounded-2xl p-6 hover:bg-rose-500/12 transition-colors duration-300">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                          <h4 className="text-xs font-semibold text-rose-400 uppercase tracking-widest">
                            What You Give Up
                          </h4>
                        </div>
                        <p className="text-slate-200/90 leading-relaxed">
                          {tradeoffLose}
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Questions */}
                {questions && questions.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.45, duration: 0.5 }}
                    className="bg-[#EAA059]/8 border border-[#EAA059]/20 rounded-2xl p-6 sm:p-8"
                  >
                    <h4 className="text-xs font-semibold text-[#EAA059]/80 uppercase tracking-widest mb-5">
                      Questions for the Vendor
                    </h4>
                    <div className="space-y-3.5">
                      {questions.map((question, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.55 + index * 0.07, duration: 0.5 }}
                          className="flex gap-3"
                        >
                          <span className="text-[#EAA059]/80 font-semibold flex-shrink-0">{index + 1}.</span>
                          <span className="text-slate-300/90">{question}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}

                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="text-center pt-2"
                >
                  <ExperienceButton
                    onClick={onNext}
                    className="px-10 py-4 bg-gradient-to-r from-[#EAA059] to-[#d4893e] hover:from-[#d4914d] hover:to-[#c07830] text-white text-lg font-medium rounded-full transition-all duration-300 shadow-lg shadow-[#EAA059]/20 hover:shadow-xl hover:shadow-[#EAA059]/30"
                  >
                    {currentIndex < totalCount - 1 ? "Next Criterion" : "View Summary"}
                    <ChevronRight className="ml-2 w-5 h-5" />
                  </ExperienceButton>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <CitationModal
        isOpen={isCitationModalOpen}
        sources={selectedCitations}
        citationIndex={selectedCitationIndex}
        onClose={handleCloseCitationModal}
      />
    </div>
  );
}
